import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAdminNotetakerHistory } from "@/hooks/agent/useGenieMeetings";

interface NylasHistorySectionProps {
  meetingId: string;
}

interface HistoryRow {
  time: string | null;
  type: string;
  states: string;
}

const pick = (source: any, ...keys: string[]): any => keys.map((key) => source?.[key]).find((value) => value != null);

/** Unix seconds (as a number or string) or an ISO date, as a local time with seconds. */
const formatHistoryTime = (value: unknown): string | null => {
  if (value == null || value === "") return null;
  const numeric = Number(value);
  const date = Number.isFinite(numeric) ? new Date(numeric * 1000) : new Date(String(value));
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "medium" });
};

/** Nylas's response shape is not pinned down, so rows are read loosely and the raw JSON is always shown too. */
const toHistoryRows = (history: unknown): HistoryRow[] => {
  const events = Array.isArray(history) ? history : pick(history, "events", "history");
  if (!Array.isArray(events)) return [];

  return events.map((event: any) => {
    const data = event?.data ?? event;
    return {
      time: formatHistoryTime(pick(event, "created_at", "createdAt", "timestamp")),
      type: String(pick(event, "event_type", "eventType", "type") ?? "event"),
      states: [pick(data, "state"), pick(data, "meeting_state", "meetingState")].filter(Boolean).join(" · "),
    };
  });
};

/**
 * Admin debug: what Nylas itself recorded for the notetaker, next to our own
 * Activity list built from the notifications it sent us. Loaded on demand, as
 * each load is a live call to Nylas.
 */
export const NylasHistorySection = ({ meetingId }: NylasHistorySectionProps) => {
  const [requested, setRequested] = useState(false);
  const { data, isFetching, isError, refetch } = useAdminNotetakerHistory(meetingId, requested);
  const rows = data?.found ? toHistoryRows(data.history) : [];

  return (
    <section>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="text-sm font-semibold text-gray-700">Nylas history</h3>
        <Button
          variant="outline"
          onClick={() => (requested ? refetch() : setRequested(true))}
          disabled={isFetching}
          className="min-h-[44px]"
        >
          {isFetching && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {requested ? "Reload" : "Load from Nylas"}
        </Button>
      </div>

      {!requested && (
        <p className="text-sm text-gray-600">
          What Nylas recorded for this notetaker, to compare with the Activity above.
        </p>
      )}
      {isError && <p className="text-sm text-red-600">Nylas's history couldn't be loaded.</p>}
      {data && !data.found && (
        <p className="text-sm text-gray-600">
          Nylas has no notetaker for this meeting any more. A completed meeting's notetaker is deleted once its notes
          are saved.
        </p>
      )}

      {data?.found && (
        <div className="space-y-4">
          {rows.length > 0 && (
            <ol className="rounded-xl border border-gray-200 px-4">
              {rows.map((row, index) => (
                <li key={index} className="py-3 border-b border-gray-100 last:border-b-0">
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    {row.time && <span className="text-xs text-gray-500 tabular-nums">{row.time}</span>}
                    <span className="text-sm font-medium text-gray-900">{row.type}</span>
                  </div>
                  {row.states && <p className="text-xs text-gray-400 mt-0.5 font-mono break-all">{row.states}</p>}
                </li>
              ))}
            </ol>
          )}
          <pre className="max-h-96 overflow-auto rounded-xl bg-gray-50 border border-gray-200 p-3 text-xs text-gray-700">
            {JSON.stringify(data.history, null, 2)}
          </pre>
        </div>
      )}
    </section>
  );
};
